import _ from '@lodash';
import localForage from 'localforage';
import { persistReducer } from 'redux-persist';
import { createSelector } from '@reduxjs/toolkit';
import { type RootState } from 'app/store';
import {
	type PrintGroup,
	type PrintGroupsState,
	type PrintGroupsActionTypes,
	type PrintGroupCardType,
} from 'app/store/types';
import { managedPrintGroupTypes } from 'app/utils/helpers';

const printGroupCardTypes = managedPrintGroupTypes('all').map((type) => ({
	id: type,
})) satisfies PrintGroupCardType[];

const initialState: PrintGroupsState = {};

const printGroupsReducer = (state = initialState, action: PrintGroupsActionTypes) => {
	switch (action.type) {
		case 'GET_LICENSE_GROUP_DATA_SUCCESS': {
			const {
				licenseGroupId,
				data: { printGroups },
			} = action.payload;

			const managedGroups = _.keyBy(printGroupCardTypes, 'id');

			let updatedGroups;
			if (printGroups) {
				// maintain order since order is not returned in that api call
				const groups = { ...state[licenseGroupId]?.byId };
				updatedGroups = Object.keys(printGroups).reduce((result: { [key: string]: PrintGroup }, id) => {
					const existingGroup = groups[id];
					result[id] = existingGroup?.groupOrder
						? {
								...printGroups[id],
								groupOrder: existingGroup.groupOrder,
							}
						: printGroups[id];
					return result;
				}, {});
			}

			let updatedManagedGroups;
			if (managedGroups) {
				// maintain order since order is not returned in that api call
				const groups = { ...state[licenseGroupId]?.cardTypesById };
				updatedManagedGroups = (Object.keys(managedGroups) as PrintGroupCardType['id'][]).reduce(
					(result, id) => {
						const existingGroup = groups[id];
						result[id] = existingGroup?.groupOrder
							? {
									...managedGroups[id],
									groupOrder: existingGroup.groupOrder,
								}
							: managedGroups[id];
						return result;
					},
					{} as { [key in PrintGroupCardType['id']]: PrintGroupCardType },
				);
			}

			return {
				...state,
				[licenseGroupId]: {
					...state[licenseGroupId],
					...(updatedGroups && { byId: updatedGroups }),
					...(updatedManagedGroups && { cardTypesById: updatedManagedGroups }),
				},
			};
		}
		case 'GET_PRINT_GROUP_ORDER_SUCCESS': {
			const { order, licenseGroupId } = action.payload;
			const groups = { ...state[licenseGroupId].byId };

			// order list may not contain all groups
			const existingGroupIds = order.filter((groupId: string) => groups[groupId]);
			const updatedGroups: { [key: string]: PrintGroup } = {};

			existingGroupIds.forEach((groupId: string, idx: number) => {
				const group = { ...groups[groupId] };
				group.groupOrder = idx + 1;
				updatedGroups[groupId] = group;
			});

			// assign other group order for non existing groups in order array
			const nonOrderedGroups = Object.keys(groups).filter((groupId) => !existingGroupIds.includes(groupId));

			nonOrderedGroups.forEach((groupId, idx) => {
				const group = { ...groups[groupId] };
				group.groupOrder = existingGroupIds.length + idx + 1;
				updatedGroups[groupId] = group;
			});

			return {
				...state,
				[licenseGroupId]: {
					...state[licenseGroupId],
					byId: updatedGroups,
				},
			};
		}
		case 'GET_MANAGED_PRINT_GROUP_ORDER_SUCCESS': {
			const { order, licenseGroupId } = action.payload;
			const groups = { ...state[licenseGroupId].cardTypesById };

			// order list may not contain all groups
			const existingGroupIds = order.filter((groupId) => groups[groupId]);
			const updatedGroups = {} as { [key in PrintGroupCardType['id']]: PrintGroupCardType };

			existingGroupIds.forEach((groupId, idx) => {
				const group = { ...groups[groupId] };
				group.groupOrder = idx + 1;
				updatedGroups[groupId] = group;
			});

			// assign other group order for non existing groups in order array
			const nonOrderedGroups = (Object.keys(groups) as PrintGroupCardType['id'][]).filter(
				(groupId) => !existingGroupIds.includes(groupId),
			);

			nonOrderedGroups.forEach((groupId, idx) => {
				const group = { ...groups[groupId] };
				group.groupOrder = existingGroupIds.length + idx + 1;
				updatedGroups[groupId] = group;
			});

			return {
				...state,
				[licenseGroupId]: {
					...state[licenseGroupId],
					cardTypesById: updatedGroups,
				},
			};
		}
		case 'LOGGED_OUT_USER':
		case 'PURGE_STATE':
			return initialState;
		default: {
			return state;
		}
	}
};

export default persistReducer(
	{
		key: 'printGroups',
		storage: localForage,
		whitelist: localStorage.getItem('persist') ? undefined : [],
	},
	printGroupsReducer,
);

// Selectors

export const getPrintGroupsById = ({ app: { selectedLicenseGroupId }, printGroups }: RootState) =>
	printGroups[selectedLicenseGroupId].byId;

export const getPrintGroups = createSelector([getPrintGroupsById], (printGroupsById) => Object.values(printGroupsById));

export const getPrintGroupById = (printGroupId: PrintGroup['id']) => (state: RootState) =>
	getPrintGroupsById(state)[printGroupId];

export const getPrintGroupCardTypesById = ({ app: { selectedLicenseGroupId }, printGroups }: RootState) =>
	printGroups[selectedLicenseGroupId].cardTypesById;

export const getPrintGroupCardTypes = createSelector([getPrintGroupCardTypesById], (printGroupCardTypesById) =>
	Object.values(printGroupCardTypesById),
);
